import trueAxios from 'axios'

import MockAxios from './mockAxios'
import {
  AddGeneratorParameter,
  CampaignCodes,
  CampaignCodesCompressed,
} from '../types'
import Make from './make'

const useMock = false

let axios: any
if (useMock) {
  axios = MockAxios
} else {
  axios = trueAxios
}

// Progressively moving to separate modules rather than a class
const make = Make()
const makeGQL = Make('', true)

interface SaveCampaignCode extends CampaignCodes {
  landingPage: string
  trackingCode: string
}

export interface SaveCampaignCodes {
  lP: string
  action:
    | 'add-code'
    | 'add-multiple-codes'
    | 'add-code-clone'
    | 'add-multiple-codes-clone'
  cL: {
    pDfs: [string, string][]
    tC: string
  }[]
}

class ApiClient {
  // eslint-disable-next-line class-methods-use-this
  private getToken(): string | boolean {
    return make.getToken()
  }

  // eslint-disable-next-line class-methods-use-this
  private getNoCache(endpoint: string, data = {}, options = {}): Promise<any> {
    return make.get({
      endpoint,
      data,
      options,
    })
  }

  // eslint-disable-next-line class-methods-use-this
  private post(endpoint: string, data = {}, options = {}): Promise<any> {
    return make.post({
      endpoint,
      data,
      options,
    })
  }

  // eslint-disable-next-line class-methods-use-this
  private delete(endpoint: string, data = {}, options = {}): Promise<any> {
    return make.delete({
      endpoint,
      data,
      options,
    })
  }

  // eslint-disable-next-line class-methods-use-this
  private put(endpoint: string, data = {}, options = {}): Promise<any> {
    return make.put({
      endpoint,
      data,
      options,
    })
  }

  // eslint-disable-next-line class-methods-use-this
  public async getVersion(): Promise<any> {
    const req = await axios({
      method: 'get',
      url: `/version.json`,
    })

    if (req && req.status === 200 && req.data) {
      return req.data
    }

    return false
  }

  // eslint-disable-next-line class-methods-use-this
  public async login({
    email: username,
    password,
  }: {
    email: string
    password: string
  }): Promise<any> {
    const req = await makeGQL.post({
      endpoint: 'auth/login',
      data: {
        username,
        password,
      },
    })

    if (!req.success) {
      return req || false
    }

    return req
  }

  public async getGooglePermissions(state: any): Promise<boolean> {
    const req = await this.post('setup/authorize', {
      ...state,
      token: this.getToken(),
    })

    if (!req.success) {
      return false
    }

    window.location = req.res.data.GOOGLE_AUTH_URL
    return true
  }

  public async forwardOauth(get: string): Promise<any | boolean> {
    const req = await this.getNoCache(`setup/oauth2callback${get}`)
    if (!req.success) {
      return false
    }

    return req.res.data.state
  }

  public async getAnomalyDataRange(
    metricID: string,
    from: string,
    to: string,
  ): Promise<any | boolean> {
    const req = await this.getNoCache(
      `explain/get-range/${metricID}/${from}/${to}`,
    )

    if (!req.success) {
      return false
    }

    return req.res
  }

  public async saveCause(
    id: string,
    date: string,
    metric: string,
    difference: number,
  ): Promise<any | boolean> {
    const req = await this.post('explain/save-cause', {
      databaseID: id,
      date,
      metric,
      difference,
    })
    if (!req.success) {
      return false
    }

    return true
  }

  public async saveAction(
    id: string,
    date: string,
    metric: string,
    difference: number,
  ): Promise<any | boolean> {
    const req = await this.post('explain/save-action', {
      databaseID: id,
      date,
      metric,
      difference,
    })
    if (!req.success) {
      return false
    }

    return true
  }

  public async getCausesAndSolutions(
    anomalyDateId: string,
  ): Promise<any | boolean> {
    const req = await this.getNoCache(
      `explain/generate-recommendations/${anomalyDateId}`,
    )

    if (!req.success) {
      return false
    }

    return req.res.data
  }

  public async getUserList(): Promise<any> {
    const req = await this.getNoCache(`account/get-user-list`)
    if (!req.success) {
      return null
    }

    return req.res.data
  }

  public async getAllUsers(): Promise<any[]> {
    const req = await this.getNoCache('site/get-users')
    if (!req.success) {
      return []
    }

    return req.res.data
  }

  // settings
  public async addUser(form: any): Promise<boolean> {
    const req = await this.post('settings/add-user', form)

    if (!req.success) {
      return false
    }

    return true
  }

  public async removeUser(form: any): Promise<boolean> {
    const req = await this.delete('settings/remove-user', form)

    if (!req.success) {
      return false
    }

    if (req.res.message === 'success') {
      return true
    }

    return false
  }

  public async closeAccounts(): Promise<boolean> {
    const req = await this.delete('settings/remove-accounts')

    if (!req.success) {
      return false
    }

    if (req.res.message === 'success') {
      return true
    }

    return false
  }

  // eslint-disable-next-line class-methods-use-this
  public logout(): boolean {
    make.destroyToken()
    window.location.reload()
    return true
  }

  // eslint-disable-next-line class-methods-use-this
  public setCredentials(credentials): boolean {
    make.setCredentials(credentials)
    return true
  }

  // eslint-disable-next-line class-methods-use-this
  public setDataSource(credentials): boolean {
    make.setDataSource(credentials)
    return true
  }

  public async getAccountSelected(): Promise<boolean | any> {
    const req = await this.getNoCache('account')

    if (!req.success) {
      return false
    }

    return req.res.data
  }

  public async getCampaignCodesByAccount(): Promise<
    CampaignCodesCompressed[] | false
  > {
    const req = await this.getNoCache('code/get-codes-by-account')
    if (!req.success) {
      return false
    }

    return req.res.data.codeList
  }

  public async updateCampaignCodeGeneratorOrder(form: any): Promise<boolean> {
    const req = await this.post('code/update-generator-parameter-order', form)
    if (!req.success) {
      return false
    }

    return true
  }

  public async updateGeneratorParameter(form: any): Promise<boolean> {
    const req = await this.post('code/update-generator-parameter', form)
    if (!req.success) {
      return false
    }

    return true
  }

  public async deleteGeneratorParameter(data: {
    removeId: string
  }): Promise<boolean> {
    const req = await this.post('code/delete-generator-parameter', data)
    if (!req.success) {
      return false
    }

    return true
  }

  public async addGeneratorParameter(
    data: AddGeneratorParameter,
  ): Promise<boolean> {
    const req = await this.post('code/add-generator-parameter', data)
    if (!req.success) {
      return false
    }

    return true
  }

  public async saveCampaignCode(form: SaveCampaignCode): Promise<boolean> {
    const req = await this.post('code/add-code', { ...form })
    if (!req.success) {
      return false
    }

    return true
  }

  public async saveCampaignCodes(form: SaveCampaignCodes): Promise<boolean> {
    const req = await this.post('code/add-codes', { ...form })
    if (!req.success) {
      return false
    }

    return true
  }

  public async getPreviousGeneratedCodes({
    startDate,
    endDate,
  }: {
    startDate?: string
    endDate?: string
  }): Promise<false | CampaignCodes[]> {
    const req = await this.getNoCache('code/get-codes-by-account', {
      params: { startDate, endDate },
    })
    if (!req.success) {
      return false
    }

    return req.res.data.codeList
  }

  public async getBatchPreviousGeneratedCodes({
    startDate,
    endDate,
  }: {
    startDate?: string
    endDate?: string
  }): Promise<false | any> {
    const req = await this.getNoCache('code/get-batch-codes-by-account', {
      params: { startDate, endDate },
    })
    if (!req.success) {
      return false
    }
    const { codeList, queryTotal } = req.res.data
    return { codeList, queryTotal }
  }

  public async getAAFTPDetails(): Promise<boolean> {
    const req = await this.getNoCache('code/get-AA-FTP-details')
    if (!req.success) {
      return false
    }
    return req.res.data
  }

  public async updateAAFTPDetails(details: FTPDetails): Promise<boolean> {
    const req = await this.put('code/update-AA-FTP-details', details)
    if (!req.success) {
      return false
    }

    return true
  }

  public async updateCampaignCode(
    form: CampaignCodesCompressed,
  ): Promise<boolean> {
    const req = await this.put('code/update-code', form)
    if (!req.success) {
      return false
    }

    return true
  }

  public async generateSaintFile(): Promise<boolean> {
    const req = await this.getNoCache('code/generate-saint-file')
    if (!req.success) {
      return false
    }

    return true
  }

  public async deleteCampaignCodes(data: string[]): Promise<boolean> {
    const postData = {
      codeList: data,
    }
    const req = await this.post(`code/delete-codes`, postData)

    if (!req.success) {
      return false
    }

    return true
  }
}

export default new ApiClient()
